import React, { useEffect, useImperativeHandle } from 'react';
import isEmpty from 'lodash/isEmpty';
import { Formik, Field, FieldArray } from 'formik';
import {
  Form,
  Segmented,
  Modal,
  Button,
  List,
  Upload as AntdUpload,
  notification,
  Spin,
} from 'antd';
import PropTypes from 'prop-types';
import { WithModal } from 'hocs/withModal';
import { AButton } from 'components/Antd';
import { InfiniteSelectField } from 'components/Molecules/FormFields/index';
import Panel from 'components/Antd/Layout/Panel/index';
import {
  apiProductUploadFileToFolder,
  apiSupplierUploadFileToFolder,
  apiFactoryUploadFileToFolder,
} from 'api';
import cn from 'classnames';
import FormObserver from 'components/New/FormStepper/FormObserver';
import { useCreate } from 'context/ResourceProvider/hooks/useCreate';
import useUploadFile from 'modules/inspection/hooks/useUploadDocument';
import get from 'lodash/get';
import classes from './QMASDocument.module.scss';
import SaveTo from './components/SubForms/SaveTo';
import DateAndReminder from './components/SubForms/DateAndReminder';
import { DocumentItem } from './components/DocumentItem/index';
import { useMe } from 'modules/company/hooks/useCompanySelector';
import T from 'i18n-react';
import { ComplianceData } from './components/ComplianceData/index';
import { transformComplianceData } from './helper';
import set from 'lodash/set';
import { QMASDocumentContext } from './context';
import Alert from 'components/Molecules/Alert/index';
import { ExclamationCircleFilled } from '@ant-design/icons';
import withLazyLoadQMASDocument from './hocs/withLazyLoadQMASDocument';

export const INITIAL_VALUES = {
  files: [],
  type: [],
  // Link Value
  saveTo: {
    suppliers: [],
    factories: [],
    products: [],
  },
  reminder: {
    issueDate: null,
    expiryDate: null,
    reviewDate: null,
    reminderDate: null,
    reminderType: null,
    note: '',
  },
  complianceData: {
    testData: [],
    certificateData: [],
    otherData: [],
  },
};

const NS = 'QMASUploadDocument';

const SETTINGS = [
  {
    label: T.translate(`${NS}.saveTo`),
    value: 'saveTo',
    name: 'saveTo',
    children: <SaveTo />,
  },
  {
    label: T.translate(`${NS}.dateAndReminder`),
    value: 'reminder',
    name: 'reminder',
    children: <DateAndReminder />,
  },
  {
    label: T.translate(`${NS}.complianceData`),
    value: 'compliance',
    name: 'compliance-data',
    children: <ComplianceData />,
  },
];

const DEFAULT_RELATED_SOURCE = {
  products: [],
  suppliers: [],
  factories: [],
  testItems: [],
};

function QMASDocument(
  {
    shouldDisabledField,
    onSubmit,
    afterReadonlyChange,
    loading = false,
    featureFlags = ['saveTo', 'reminder'],
    relatedSource = DEFAULT_RELATED_SOURCE,
    initialValues = INITIAL_VALUES,
    fileAction = {
      deletable: true,
      renamable: true,
      shouldShowFileType: true,
    },
    showDocumentList = true,
    readonly = false,
    hiddenSegmented = false,
  },
  ref
) {
  const [stateReadOnly, setReadOnly] = React.useState(readonly);
  const [creating, setCreating] = React.useState(false);
  const formRef = React.useRef();
  const [modal, contextHolder] = Modal.useModal();
  const [currentSetting, setCurrentSetting] = React.useState('saveTo');
  const [animClass, setAnimClass] = React.useState('fade-in');
  const { mutate } = useCreate({
    resource: 'document-list/types',
  });

  const showResetReminder = (onClear, onCancel) => {
    const { destroy } = modal.confirm({
      title: T.translate(`${NS}.areYouSureToReset`),
      content: T.translate(`${NS}.reminderNoteWillBeCleared`),
      okText: T.translate(`${NS}.clear`),
      cancelText: T.translate(`${NS}.cancel`),
      cancelButtonProps: { hidden: true },
      onOk: () => {
        onClear();
        destroy();
      },
      onCancel: () => {
        onCancel();
        destroy();
      },
    });
  };

  const handleShowConfirm = (date, prevDate, onClear, onCancel) => {
    if (!date && prevDate) {
      showResetReminder(onClear, onCancel);
    }
  };

  const handleResetReminderField = (values, prevValues) => {
    if (values.reminder.reminderType !== prevValues.reminder.reminderType) {
      formRef.current.setFieldValue('reminder.reminderDate', null);
      formRef.current.setFieldValue('reminder.note', null);
    }
  };

  const handleValueChange = (...rest) => {
    const [changedValue, values, prevValues, formik] = rest;
    const { expiryDate, reviewDate, reminderType } = values.reminder;
    const onClear = () => {
      formik.setFieldValue('reminder.reminderType', null);
      formik.setFieldValue('reminder.reminderDate', null);
      formik.setFieldValue('reminder.note', null);
      formik.setFieldValue('reminder.reminderDate', null);
    };
    if (reminderType === 'onExpiryDate') {
      handleShowConfirm(expiryDate, prevValues.reminder.expiryDate, onClear, () => {
        // Cancel confirm.
        formik.setFieldValue('reminder.emailReminder', prevValues.reminder.reminderType);
        formik.setFieldValue('reminder.expiryDate', prevValues.reminder.expiryDate);
        formik.setFieldValue('reminder.note', null);
        formik.setFieldValue('reminder.reminderDate', null);
      });
    }
    if (reminderType === 'onReviewDate') {
      handleShowConfirm(reviewDate, prevValues.reminder.reviewDate, onClear, () => {
        formik.setFieldValue('reminder.reminderType', prevValues.reminder.reminderType);
        formik.setFieldValue('reminder.reviewDate', prevValues.reminder.reviewDate);
        formik.setFieldValue('reminder.note', null);
        formik.setFieldValue('reminder.reminderDate', null);
      });
    }
    handleResetReminderField(values, prevValues);
  };

  const handleCreateNewType = async (newOption, { onChange, onSelect, mode, value, ...rest }) => {
    setCreating(true);
    onChange([newOption]);
    const response = await mutate({
      type: 'documentType',
      name: newOption.label,
    });
    const data = get(response, 'data.data');
    if (data) {
      onChange({
        label: data.name,
        value: data.id,
      });
      onSelect();
    } else {
      notification.error({
        message: 'Create new type failed',
      });
    }
    setCreating(false);
  };

  useEffect(() => {
    const currentFeatures = SETTINGS.filter(({ name }) => featureFlags.includes(name));
    const selectedCurrentSetting = currentFeatures.find(({ value }) => value === currentSetting);

    if (!selectedCurrentSetting) {
      setCurrentSetting(currentFeatures[0] ? currentFeatures[0].value : '');
    }
  }, [featureFlags]);

  useImperativeHandle(ref, () => ({
    submit: () => {
      return formRef.current.submitForm();
    },
  }));
  return (
    <QMASDocumentContext.Provider value={{ readonly: stateReadOnly, shouldDisabledField }}>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(...rest) => {
          onSubmit(...rest, stateReadOnly);
        }}>
        {(formikProps) => {
          formRef.current = formikProps;
          return (
            <Spin spinning={loading}>
              <Form layout="vertical" className={classes.QMASDocument}>
                <Alert
                  style={{ marginTop: 8 }}
                  visible={stateReadOnly}
                  showIcon
                  icon={<ExclamationCircleFilled />}
                  type="error"
                  message="Warning: Modifying below data will impact all entities associated with this document."
                  action={
                    <Button
                      type="primary"
                      size="small"
                      danger
                      ghost
                      onClick={() => {
                        setReadOnly(false);
                        if (afterReadonlyChange) {
                          afterReadonlyChange(false);
                        }
                      }}>
                      Enter Edit Mode
                    </Button>
                  }></Alert>
                <FormObserver onChange={handleValueChange} />
                <Field name="files">
                  {({ field }) => {
                    if (!showDocumentList) {
                      return null;
                    }
                    return (
                      <FieldArray name={field.name}>
                        {({ remove, replace }) => {
                          return (
                            <List
                              split={false}
                              dataSource={field.value}
                              rowKey={({ id }) => id}
                              renderItem={(record, idx) => {
                                let isDeletable = fileAction.deletable
                                  ? field.value && field.value.length > 1
                                  : false;
                                if (stateReadOnly) {
                                  isDeletable = !stateReadOnly;
                                }

                                return (
                                  <DocumentItem
                                    renamable={stateReadOnly ? false : fileAction.renamable}
                                    item={record}
                                    deletable={isDeletable}
                                    onRename={(newFileName) => {
                                      const { originFileObj } = record;
                                      if (originFileObj) {
                                        const fileWithNewName = new File(
                                          [originFileObj],
                                          newFileName,
                                          {
                                            type: originFileObj.type,
                                          }
                                        );
                                        replace(idx, {
                                          ...record,
                                          name: newFileName,
                                          originFileObj: fileWithNewName,
                                        });
                                      } else {
                                        replace(idx, {
                                          ...record,
                                          name: newFileName,
                                        });
                                      }
                                    }}
                                    onRemove={() => {
                                      remove(idx);
                                    }}
                                  />
                                );
                              }}
                            />
                          );
                        }}
                      </FieldArray>
                    );
                  }}
                </Field>
                {fileAction.shouldShowFileType && (
                  <InfiniteSelectField
                    disabled={stateReadOnly}
                    name="type"
                    label={T.translate(`${NS}.type`)}
                    placeholder={T.translate(`placeholder.fillOrSelect`)}
                    resource="document-list/types"
                    creatable
                    showArrow
                    onCreate={handleCreateNewType}
                    loading={creating}
                  />
                )}

                <Segmented
                  hidden={hiddenSegmented}
                  options={SETTINGS.filter(({ name }) => {
                    if (featureFlags.includes(name)) {
                      return true;
                    }
                    return false;
                  }).map(({ label, value }) => ({ label, value }))}
                  onChange={(value) => {
                    setAnimClass('fade-out');
                    setTimeout(() => {
                      setCurrentSetting(value);
                      setAnimClass('fade-in');
                    }, 500); // Duration of the fade-out animation
                  }}
                />
                <Panel
                  contentClassName={cn('icw-scrollbar', classes.settingContainer, {
                    [classes.ghost]: currentSetting === 'compliance',
                  })}>
                  <div
                    className={cn({
                      [classes.fadeIn]: animClass === 'fade-in',
                      [classes.fadeOut]: animClass === 'fade-out',
                    })}>
                    {SETTINGS.map(({ value, children }) => {
                      if (value === currentSetting) {
                        return React.cloneElement(children, { relatedSource });
                      }
                      return null;
                    })}
                  </div>
                  {contextHolder}
                </Panel>
              </Form>
            </Spin>
          );
        }}
      </Formik>
    </QMASDocumentContext.Provider>
  );
}

QMASDocument.propTypes = {
  // multiple: PropTypes.bool,
  initialValues: PropTypes.objectOf({}), // TODO Declare the shape of the object
};

QMASDocument.defaultProps = {
  // multiple: false,
  initialValues: INITIAL_VALUES,
};
export default QMASDocument;

export const QMASDocumentModal = WithModal(React.forwardRef(QMASDocument));

export const LazyQMASDocumentModal = WithModal(
  withLazyLoadQMASDocument(React.forwardRef(QMASDocument))
);

export const QMASDocumentUpload = ({
  products,
  suppliers,
  factories,
  onUploadSuccess,
  buttonProps,
  parentId,
  parentModule,
  featureFlags = ['saveTo', 'reminder'],
  relatedSource = DEFAULT_RELATED_SOURCE,
  actionCb,
}) => {
  const { company } = useMe();
  const [submitting, setSubmitting] = React.useState(false);
  const [showForm, setShowForm] = React.useState(false);
  const [documents, setDocuments] = React.useState([]);
  const formRef = React.useRef();
  const { uploadFile } = useUploadFile();
  const { mutate } = useCreate({
    resource: 'document-list/document-list-index',
  });

  const handleUploadFiles = async (values) => {
    const { files } = values;
    const willUpload = files
      .filter(({ originFileObj }) => originFileObj)
      .map(({ originFileObj }) => originFileObj);

    const uploaded = files.filter(({ originFileObj }) => !originFileObj);
    try {
      if (willUpload.length) {
        const uploadedFiles = await uploadFile(willUpload, company.companyId);
        return [...uploaded, ...(uploadedFiles || [])].map(({ id }) => id);
      }
      return uploaded.map(({ id }) => id);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    const { files, type, saveTo, reminder, complianceData } = values;
    if (!files.length) {
      notification.error({
        message: 'No Document To Upload',
      });
      return;
    }
    try {
      const documentListIds = await handleUploadFiles(values);
      const supplierIds = saveTo.suppliers.map(({ id }) => id);
      const factoryIds = saveTo.factories.map(({ id }) => id);
      const productIds = saveTo.products.map(({ id }) => id);
      const payload = {
        documentListIds,
        supplierIds,
        factoryIds,
        productIds,
        tagIds: type
          ? [type].map((option) => {
              if (option && option.value) {
                return option.value;
              }
              return option;
            })
          : [],
        issueDate: reminder.issueDate ? reminder.issueDate.format('YYYY-MM-DD') : null,
        expiryDate: reminder.expiryDate ? reminder.expiryDate.format('YYYY-MM-DD') : null,
        reviewDate: reminder.reviewDate ? reminder.reviewDate.format('YYYY-MM-DD') : null,
        reminderDate: reminder.reminderDate ? reminder.reminderDate.format('YYYY-MM-DD') : null,
        reminderType: reminder.reminderType,
        note: reminder.note,
        complianceData: transformComplianceData({
          testData: [],
          certificateData: [],
          otherData: [],
          ...complianceData,
        }),
      };
      const response = await mutate(payload);
      if (parentId) {
        try {
          const apiUpload =
            parentModule === 'supplier'
              ? apiSupplierUploadFileToFolder
              : parentModule === 'factory'
              ? apiFactoryUploadFileToFolder
              : apiProductUploadFileToFolder;
          const tModuleId =
            parentModule === 'supplier'
              ? supplierIds[0]
              : parentModule === 'factory'
              ? factoryIds[0]
              : productIds[0];
          await apiUpload(tModuleId, {
            parentId,
            documentListIds: documentListIds,
          });
        } catch (error) {}
      }
      const data = get(response, 'data.data');
      notification.success({
        message: 'Upload Document Succeed',
        placement: 'bottomLeft',
      });
      if (onUploadSuccess) {
        onUploadSuccess(data);
      }
      setShowForm(false);

      setDocuments([]);
      setSubmitting(false);
    } catch (error) {
      if (error.name === 'FILE_SIZE_LIMIT') {
        notification.error({
          message: error.message,
        });
      } else {
        notification.error({
          message: 'Upload Document Failed',
        });
      }

      setSubmitting(false);
    }
  };
  const handleShowUploadModal = (tFiles) => {
    if (tFiles?.length) {
      setDocuments([...tFiles]);
      setShowForm(true);
    }
  }
  if (actionCb) {
    actionCb({
      onShowUploadModal: handleShowUploadModal,
    })
  }

  return (
    <React.Fragment>
      <div className={classes.buttonGroup}>
        {!isEmpty(buttonProps?.newFolderButtonProps) && (
          <AButton type="primary" {...(buttonProps?.newFolderButtonProps || {})}>
            New Folder
          </AButton>
        )}
        <AntdUpload
          multiple
          beforeUpload={() => false}
          fileList={documents}
          onChange={({ fileList }) => {
            setDocuments([...fileList]);
            setShowForm(!!fileList.length);
          }}
          showUploadList={false}>
          <AButton type="primary" {...(buttonProps?.uploadButtonProps || {})}>
            {T.translate(`${NS}.upload`)}
          </AButton>
        </AntdUpload>
      </div>
      <QMASDocumentModal
        title={T.translate(`${NS}.uploadDocument`)}
        width={'80vw'}
        wrapClassName={classes.overlapModal}
        loading={submitting}
        destroyOnClose
        open={showForm}
        onCancel={() => {
          setShowForm(false);
          setDocuments([]);
        }}
        initialValues={{
          ...INITIAL_VALUES,
          saveTo: {
            products: (products || []).map(({ ...rest }) => ({ ...rest, deletable: false })),
            suppliers: (suppliers || []).map(({ ...rest }) => ({ ...rest, deletable: false })),
            factories: (factories || []).map(({ ...rest }) => ({ ...rest, deletable: false })),
          },
          files: documents,
        }}
        childRef={formRef}
        onOk={() => {
          formRef.current.submit();
        }}
        okText={T.translate('general.confirm')}
        okButtonProps={{ loading: submitting }}
        cancelButtonProps={{
          hidden: true,
        }}
        onSubmit={handleSubmit}
        relatedSource={relatedSource}
        featureFlags={featureFlags}
      />
    </React.Fragment>
  );
};
